import * as React from 'react';

import config from 'js/app/config';

import type { MergedDegreeDescriptionPageFields } from 'bundles/degree-description/contexts/DegreeDescriptionPageContext';
import type { UpsellsByDegreeSlug } from 'bundles/degree-description/types';
import {
  PAGE_NAMES_MARKUP,
  getBreadcrumbListMarkupData,
  getCredentialCategoryMarkupData,
  getProviderMarkupData,
} from 'bundles/degree-description/utils/schema-markup';
import { filterNullOrUndefined, getUpsellInfo } from 'bundles/degree-description/utils/utils';
import { stringifyJsonLd } from 'bundles/seo/utils/utils';

type Props = {
  pageName: (typeof PAGE_NAMES_MARKUP)[keyof typeof PAGE_NAMES_MARKUP];
  pathName: string;
  pageContent: MergedDegreeDescriptionPageFields;
  stackableItemsWithUpsellType?: UpsellsByDegreeSlug;
};

const SchemaMarkup = ({ pageName, pathName, pageContent, stackableItemsWithUpsellType }: Props) => {
  const { programName, slug, partner, tuitionCurrency, tuitionPrice } = pageContent;

  const generateSchemaMarkupObj = () => {
    const breadcrumbMarkup = getBreadcrumbListMarkupData(pageContent, {
      pageName,
      pathName,
    });
    const credentialCategoryMarkup = getCredentialCategoryMarkupData();
    const providerMarkup = getProviderMarkupData(pageContent);

    const OVERVIEW_PAGE_MARKUP = {
      '@context': 'http://schema.org',
      '@graph': [
        breadcrumbMarkup,
        {
          '@id': `${config.url.base}degrees/${slug}#degree`,
          '@type': 'Course',
          name: programName,
          url: `${config.url.base}degrees/${slug}`,
          image: partner?.partnerLogo?.url,
          educationalCredentialAwarded: {
            '@type': 'EducationalOccupationalCredential',
            credentialCategory: credentialCategoryMarkup,
          },
          hasCourseInstance: {
            '@type': 'CourseInstance',
            courseMode: ['distance learning', 'online'],
            startDate: '2019-08-26T00:00:00.000Z',
            location: {
              '@type': 'Place',
              name: 'Online',
              address: 'Online',
            },
            offers: {
              '@type': 'Offer',
              url: `${config.url.base}degrees/${slug}`,
              name: `Apply for ${programName}`,
              priceCurrency: tuitionCurrency,
              price: tuitionPrice,
              availability: 'OnlineOnly',
            },
          },
          provider: providerMarkup,
        },
      ],
    };

    const ADMISSIONS_PAGE_MARKUP = {
      '@context': 'http://schema.org',
      '@graph': [
        breadcrumbMarkup,
        {
          '@id': `${config.url.base}degrees/${slug}#degree`,
          '@type': 'Course',
          name: programName,
          url: `${config.url.base}degrees/${slug}`,
          image: partner?.partnerLogo?.url,
          educationalCredentialAwarded: {
            '@type': 'EducationalOccupationalCredential',
            credentialCategory: credentialCategoryMarkup,
          },
          hasCourseInstance: {
            '@type': 'CourseInstance',
            courseMode: ['distance learning', 'online'],
            startDate: '2019-08-26T00:00:00.000Z',
            location: {
              '@type': 'Place',
              name: 'Online',
              address: 'Online',
            },
          },
          provider: providerMarkup,
        },
      ],
    };

    const ACADEMICS_PAGE_MARKUP = {
      '@context': 'http://schema.org',
      '@graph': [
        breadcrumbMarkup,
        {
          '@id': `${config.url.base}degrees/${slug}#degree`,
          '@type': 'EducationalOccupationalCredential',
          name: programName,
          url: `${config.url.base}degrees/${slug}`,
          image: partner?.partnerLogo?.url,
          credentialCategory: credentialCategoryMarkup,
          hasPart:
            stackableItemsWithUpsellType &&
            stackableItemsWithUpsellType
              .map((upsell) => {
                const upsellInfo = getUpsellInfo([upsell]);
                if (!upsellInfo?.upsell) {
                  return null;
                }

                return {
                  '@id': upsellInfo.url,
                  '@type': 'Course',
                  name: upsellInfo?.upsell.name,
                  description: upsellInfo.tagline,
                  provider: {
                    '@id': `${config.url.base}${partner?.partnerSlug}#institution`,
                  },
                };
              })
              .filter(filterNullOrUndefined),
          provider: providerMarkup,
        },
      ],
    };

    const OTHER_PAGE_MARKUP = { '@context': 'http://schema.org', ...breadcrumbMarkup };

    switch (pageName) {
      case PAGE_NAMES_MARKUP.overview:
        return OVERVIEW_PAGE_MARKUP;
      case PAGE_NAMES_MARKUP.academics:
        return ACADEMICS_PAGE_MARKUP;
      case PAGE_NAMES_MARKUP.admissions:
        return ADMISSIONS_PAGE_MARKUP;
      default:
        return OTHER_PAGE_MARKUP;
    }
  };

  const createMarkup = () => {
    const markupObject = generateSchemaMarkupObj();

    return {
      __html: `
        <script type="application/ld+json">
          ${stringifyJsonLd(markupObject)}
        </script>
      `,
    };
  };

  const markUp = createMarkup();
  return (
    <div className="rc-DegreeGoogleSchemaMarkup">
      {/* eslint-disable react/no-danger */}
      <div dangerouslySetInnerHTML={markUp} />
    </div>
  );
};

export default SchemaMarkup;
